// Merchant team page — owner only. Invite, edit role/status, disable,
// re-issue reset, clear MFA. Last-owner + self-modify guards are
// enforced by the backend; the UI surfaces the resulting 409s.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  ApiError,
  merchantAuth,
  merchantUsers,
  type MerchantPeer,
  type MerchantRole,
} from '@/api/client';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ROLES: MerchantRole[] = ['owner', 'developer', 'finance', 'viewer'];

export const Route = createFileRoute('/m/users')({
  component: MerchantUsersPage,
});

function MerchantUsersPage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ['merchant-me'], queryFn: merchantAuth.me });
  const list = useQuery({ queryKey: ['merchant-peers'], queryFn: merchantUsers.list });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<MerchantPeer | null>(null);

  if (me.data && me.data.role !== 'owner') {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('merchant.users.title', { defaultValue: 'Team' })}
        </h1>
        <Empty
          title={t('merchant.users.forbidden', {
            defaultValue: 'Only the owner role can manage team members.',
          })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('merchant.users.title', { defaultValue: 'Team' })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('merchant.users.subtitle', {
              defaultValue:
                'Invite users and assign roles. Each invite triggers a one-time reset link sent to the email or mobile you provide.',
            })}
          </p>
        </div>
        <Button onClick={() => setInviting(true)}>
          {t('merchant.users.invite', { defaultValue: 'Invite user' })}
        </Button>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('users.col_username', { defaultValue: 'Username' })}</TableHead>
              <TableHead>{t('merchant.users.col_full_name', { defaultValue: 'Name' })}</TableHead>
              <TableHead>{t('users.col_role', { defaultValue: 'Role' })}</TableHead>
              <TableHead>{t('users.col_status', { defaultValue: 'Status' })}</TableHead>
              <TableHead>{t('users.col_mfa', { defaultValue: 'MFA' })}</TableHead>
              <TableHead className="text-end">
                {t('users.col_actions', { defaultValue: 'Actions' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.full_name}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-[10px] uppercase">
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={u.status === 'active' ? 'secondary' : 'outline'}
                    className="text-[10px] uppercase"
                  >
                    {u.status}
                  </Badge>
                </TableCell>
                <TableCell>{u.mfa_enabled ? '✓' : '—'}</TableCell>
                <TableCell className="text-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={me.data?.id === u.id}
                    onClick={() => setEditing(u)}
                  >
                    {t('users.edit', { defaultValue: 'Edit' })}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {editing && (
        <EditPeerDialog
          peer={editing}
          isSelf={me.data?.id === editing.id}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function InviteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState<MerchantRole>('viewer');

  const submit = useMutation({
    mutationFn: () =>
      merchantUsers.invite({
        username,
        full_name: fullName,
        email: email || undefined,
        mobile: mobile || undefined,
        role,
      }),
    onSuccess: () => {
      toast.success(
        t('merchant.users.invited', {
          defaultValue: 'Invite sent. The user will receive a setup link.',
        }),
      );
      qc.invalidateQueries({ queryKey: ['merchant-peers'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Invite failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('merchant.users.invite', { defaultValue: 'Invite user' })}
            </DialogTitle>
            <DialogDescription>
              {t('merchant.users.invite_hint', {
                defaultValue:
                  'Provide an email or mobile so they can receive their setup link.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('users.col_username', { defaultValue: 'Username' })}</Label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.full_name', { defaultValue: 'Full name' })}</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.email', { defaultValue: 'Email' })}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('merchant.profile.mobile', { defaultValue: 'Mobile (E.164)' })}</Label>
            <Input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="+218..."
            />
          </div>
          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as MerchantRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending
                ? t('common.saving', { defaultValue: 'Saving…' })
                : t('merchant.users.invite_submit', { defaultValue: 'Send invite' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditPeerDialog({
  peer,
  isSelf,
  onClose,
}: {
  peer: MerchantPeer;
  isSelf: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [role, setRole] = useState<MerchantRole>(peer.role);
  const [status, setStatus] = useState<'active' | 'disabled'>(peer.status);

  const update = useMutation({
    mutationFn: () => merchantUsers.update(peer.id, { role, status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-peers'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const disable = useMutation({
    mutationFn: () => merchantUsers.disable(peer.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant-peers'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Disable failed'),
  });

  const reissue = useMutation({
    mutationFn: () => merchantUsers.issueReset(peer.id),
    onSuccess: (r) =>
      toast.success(
        t('merchant.users.reset_sent', {
          defaultValue: 'Reset sent via {{channel}}.',
          channel: r.sent_to_channel,
        }),
      ),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Send failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isSelf) return;
            update.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('users.edit_title', {
                defaultValue: 'Edit {{name}}',
                name: peer.username,
              })}
            </DialogTitle>
            {isSelf && (
              <DialogDescription className="text-warning-foreground">
                {t('users.cannot_edit_self', {
                  defaultValue: "You can't change your own role here. Use the profile page.",
                })}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('users.col_role', { defaultValue: 'Role' })}</Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as MerchantRole)}
              disabled={isSelf}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('users.col_status', { defaultValue: 'Status' })}</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as 'active' | 'disabled')}
              disabled={isSelf}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">active</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => reissue.mutate()}
                disabled={isSelf || reissue.isPending}
              >
                {t('merchant.users.reset', { defaultValue: 'Send reset' })}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => disable.mutate()}
                disabled={isSelf || peer.status === 'disabled' || disable.isPending}
              >
                {t('users.disable', { defaultValue: 'Disable' })}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={isSelf || update.isPending}>
                {update.isPending
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
